import { useEffect } from "react";
import {
  AlertTriangle,
  Archive,
  ArrowUpDown,
  AtSign,
  CalendarClock,
  CalendarPlus,
  CalendarRange,
  ChevronsDownUp,
  ChevronsUpDown,
  Columns3,
  FileDown,
  FileSpreadsheet,
  ImageDown,
  Flag,
  FolderGit2,
  GripVertical,
  History,
  List,
  Loader2,
  LayoutTemplate,
  MessageCircle,
  MessageCircleQuestion,
  MoreHorizontal,
  Pencil,
  Plus,
  Sparkles,
  Terminal,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/misc";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Hint } from "@/components/ui/tooltip";
import { boardEndMs, formatDateTime, progressPercent, remainingLabel, urgencyColor } from "@/lib/format";
import { DURATION_LABELS, kindColor, type BoardDetail } from "@/lib/types";
import { useCan } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { isTaskSort, sortLabel, TASK_SORTS, type TaskSort } from "@/lib/task-sort";

export type BoardLayout = "board" | "list";

/** A key press that is the user typing, or aimed at something already open. */
const isTypingOrBusy = (event: KeyboardEvent): boolean => {
  const target = event.target;
  if (
    target instanceof HTMLElement &&
    (target.isContentEditable || target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT")
  ) {
    return true;
  }
  // A dialog, sheet or menu is open: `N` belongs to it, not to the board under it.
  return document.querySelector('[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]') !== null;
};

/**
 * The header names the board and when it ends, and holds two controls: the layout
 * toggle and ⋯. Everything that adds to the board — a card, a state, an import —
 * lives in ⋯ (a card is also the "+" on every state and the `N` key), so the header
 * weighs the same on a phone as on a desktop. The meta row says the deadline once,
 * the directory as a chip, and only the counts the state chips and column headers do
 * not already show.
 */
export function BoardHeader({
  detail,
  now,
  onAddTask,
  onAddColumn,
  onArchive,
  onDelete,
  onExport,
  onOpenIntake,
  onOpenWhatsApp,
  onSetProject,
  onConnectClaude,
  onEdit,
  layout,
  onLayoutChange,
  sort,
  onSortChange,
  titleInTopBar,
  waitingCount = 0,
  waitingOnly = false,
  onToggleWaiting,
  onArrange,
  onCollapseAll,
  onExportPng,
}: {
  detail: BoardDetail;
  now: number;
  /** Columns side by side, or stacked states — the latter for a phone or a tablet. */
  layout: BoardLayout;
  onLayoutChange: (layout: BoardLayout) => void;
  /** How the columns order their cards, remembered per board on this device. */
  sort: TaskSort;
  onSortChange: (sort: TaskSort) => void;
  onAddTask: () => void;
  onAddColumn: () => void;
  onArchive: () => void;
  onDelete: () => void;
  onExport: (format: "csv" | "xlsx") => void;
  /** Opens the intake chat — paste raw material, get cards. */
  onOpenIntake: () => void;
  /** Opens the board's WhatsApp chats — upload an export, get cards for what is new. */
  onOpenWhatsApp: () => void;
  /** Opens the picker for the directory this board's work happens in. */
  onSetProject: () => void;
  /** Shows how to give a Claude Code session in another project this board. */
  onConnectClaude: () => void;
  /** Opens the board editor — name, deadline, description, project. */
  onEdit: () => void;
  /** The app's top bar already names the board (the tab-bar layout). */
  titleInTopBar: boolean;
  /** Cards on this board waiting on the user's answer; 0 hides the chip. */
  waitingCount?: number;
  /** Whether the board is showing only those cards. */
  waitingOnly?: boolean;
  onToggleWaiting?: () => void;
  /**
   * A canvas board: clears every dragged position so the map is laid out afresh.
   * Given only for a canvas, whose header keeps the name, the deadline and ⋯ but
   * none of what is about columns and cards (layout, sort, new card, imports, export).
   */
  onArrange?: () => void;
  /** A canvas board: collapse or expand every node. */
  onCollapseAll?: (collapsed: boolean) => void;
  /** A canvas board: download the whole map as a PNG. */
  onExportPng?: () => void;
}) {
  // Core refuses a viewer's writes anyway; disabling says so before they try.
  const canEdit = useCan("member");
  const isAdmin = useCan("admin");
  const { board, window: boardWindow, stats } = detail;
  const expired = boardEndMs(board.endsAt) < now;
  const canvas = board.kind === "canvas";
  // Core refuses a card on an archived board, so the ways to add one are disabled;
  // a canvas has no cards of its own at all.
  const canAddTask = canEdit && !board.archived && !canvas;
  const importsOpen = detail.intake.open + detail.whatsapp.open;
  const importsWorking = detail.intake.working || detail.whatsapp.working;

  // `N` opens a new card, as the header's button once did.
  useEffect(() => {
    if (!canAddTask) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat) return;
      if (event.key !== "n" && event.key !== "N") return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      if (isTypingOrBusy(event)) return;
      event.preventDefault();
      onAddTask();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canAddTask, onAddTask]);

  const controls = (
    <div className="flex shrink-0 items-center gap-1.5">
      {canvas ? (
        onArrange && canEdit && !board.archived ? (
          <Hint label="Auto-arrange — forget where nodes were dragged and lay the map out afresh">
            <Button variant="outline" size="sm" onClick={onArrange} aria-label="Auto-arrange">
              <LayoutTemplate />
              <span className="hidden win-sm:inline">Auto-arrange</span>
            </Button>
          </Hint>
        ) : null
      ) : (
        <>
          <div role="group" aria-label="Layout" className="inline-flex rounded-md border border-border/80 p-0.5">
            {(
              [
                ["board", Columns3, "Board — states side by side"],
                ["list", List, "List — states stacked, for a narrow screen"],
              ] as const
            ).map(([value, Icon, label]) => (
              <Hint key={value} label={label}>
                <button
                  type="button"
                  aria-label={label}
                  aria-pressed={layout === value}
                  onClick={() => onLayoutChange(value)}
                  className={cn(
                    "grid size-7 place-items-center rounded-[5px] text-muted-foreground transition-colors hover:text-foreground",
                    layout === value && "bg-accent text-foreground",
                  )}
                >
                  <Icon className="size-4" />
                </button>
              </Hint>
            ))}
          </div>
          <SortMenu sort={sort} onSortChange={onSortChange} />
        </>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={importsOpen > 0 ? `Board options — ${importsOpen} import(s) in progress` : "Board options"}
            className="relative"
          >
            <MoreHorizontal />
            {/* The Paste and WhatsApp buttons used to carry this; an import under way
                should still be visible without opening the menu. */}
            {importsOpen > 0 ? (
              <span aria-hidden className="absolute right-1.5 top-1.5 size-2 rounded-full bg-primary ring-2 ring-background" />
            ) : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-56">
          <DropdownMenuLabel>{board.id}</DropdownMenuLabel>
          {canEdit && canvas ? (
            <>
              <DropdownMenuItem onSelect={() => onCollapseAll?.(true)} disabled={board.archived}>
                <ChevronsDownUp /> Collapse all
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onCollapseAll?.(false)} disabled={board.archived}>
                <ChevronsUpDown /> Expand all
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onEdit}>
                <Pencil /> Edit board…
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onSetProject}>
                <FolderGit2 /> {detail.project ? "Change project" : "Set a project"}
              </DropdownMenuItem>
            </>
          ) : canEdit ? (
            <>
              <DropdownMenuItem onSelect={onAddTask} disabled={!canAddTask}>
                <Plus /> New task…
                <span className="ml-auto pl-3 text-[11px] text-muted-foreground">N</span>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onAddColumn}>
                <Columns3 /> Add state…
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Import</DropdownMenuLabel>
              <DropdownMenuItem onSelect={onOpenIntake} disabled={board.archived}>
                {detail.intake.working ? <Loader2 className="animate-spin" /> : <Sparkles />} Import from paste…
                <ImportCount count={detail.intake.open} />
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onOpenWhatsApp} disabled={board.archived}>
                {detail.whatsapp.working ? <Loader2 className="animate-spin" /> : <MessageCircle />} Import from WhatsApp…
                <ImportCount count={detail.whatsapp.open} />
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onEdit}>
                <Pencil /> Edit board…
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onSetProject}>
                <FolderGit2 /> {detail.project ? "Change project" : "Set a project"}
              </DropdownMenuItem>
            </>
          ) : null}
          {/* The config carries the database URL, password included. */}
          {isAdmin ? (
            <DropdownMenuItem onSelect={onConnectClaude}>
              <Terminal /> Connect Claude Code…
            </DropdownMenuItem>
          ) : null}
          {canvas ? (
            onExportPng ? (
              <DropdownMenuItem onSelect={onExportPng}>
                <ImageDown /> Export as PNG
              </DropdownMenuItem>
            ) : null
          ) : (
            <>
              <DropdownMenuItem onSelect={() => onExport("xlsx")}>
                <FileSpreadsheet /> Export to Excel
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onExport("csv")}>
                <FileDown /> Export as CSV
              </DropdownMenuItem>
            </>
          )}
          {canEdit ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onArchive}>
                <Archive /> {board.archived ? "Unarchive board" : "Archive board"}
              </DropdownMenuItem>
            </>
          ) : null}
          {isAdmin ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem destructive onSelect={onDelete}>
                <Trash2 /> Delete board
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const meta = (
    <MetaRow
      barBelow={titleInTopBar}
      detail={detail}
      now={now}
      canEdit={canEdit}
      onSetProject={onSetProject}
      waiting={
        waitingCount > 0 && onToggleWaiting ? (
          <WaitingChip count={waitingCount} active={waitingOnly} onToggle={onToggleWaiting} />
        ) : null
      }
      importsWorking={importsWorking}
    />
  );

  return (
    <header className="shrink-0 border-b border-border/70 bg-background/85 px-4 py-2.5 backdrop-blur win-sm:px-6 win-sm:py-3">
      {titleInTopBar ? (
        // The top bar already names the board, so the meta row takes the title's
        // place beside the controls and the phone's first screen stays cards.
        <>
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">{meta}</div>
            {controls}
          </div>
          <DeadlineBar detail={detail} now={now} className="mt-2 win-md:hidden" />
        </>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <h1 className="truncate text-lg font-semibold tracking-tight">{board.name}</h1>
              <Hint label="Edit board — name, deadline, project" side="bottom">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onEdit}
                  aria-label="Edit board"
                  className="size-6 shrink-0 text-muted-foreground hover:text-foreground"
                >
                  <Pencil className="size-3.5" />
                </Button>
              </Hint>
              {board.archived ? <Badge variant="outline">archived</Badge> : null}
              {expired ? (
                <Badge variant="destructive" className="gap-1">
                  <AlertTriangle /> window closed
                </Badge>
              ) : null}
            </div>
            {controls}
          </div>
          {board.description ? (
            <p className="mt-0.5 line-clamp-1 text-[13px] text-muted-foreground">{board.description}</p>
          ) : null}
          <div className="mt-2">{meta}</div>
        </>
      )}
    </header>
  );
}

/**
 * When and where: the window (with a bar of how much of it has burned, only when
 * there is a deadline to burn towards), the project as a chip, and the counts no
 * state chip or column header already carries.
 */
function MetaRow({
  detail,
  now,
  canEdit,
  onSetProject,
  waiting,
  importsWorking,
  barBelow = false,
}: {
  detail: BoardDetail;
  now: number;
  canEdit: boolean;
  onSetProject: () => void;
  waiting: React.ReactNode;
  importsWorking: boolean;
  /** Below `md` the deadline bar is drawn under the row rather than in it. */
  barBelow?: boolean;
}) {
  const { board, window: boardWindow, stats } = detail;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
      {/* First and never hidden on a phone: of everything in this header it is the
          one thing asking the user to do something. */}
      {waiting}

      <Hint
        label={
          board.endsAt
            ? `${DURATION_LABELS[board.durationKind]} · closes ${formatDateTime(board.endsAt)}`
            : "No deadline: cards are due only when given a date"
        }
      >
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <CalendarRange className="size-3.5 shrink-0" />
          {boardWindow.label}
        </span>
      </Hint>

      <ProjectChip detail={detail} canEdit={canEdit} onSetProject={onSetProject} />

      {/* Inline when there is room; on a phone it is a line of its own under the row,
          drawn by the header, so the row beside the controls never wraps. */}
      <DeadlineBar detail={detail} now={now} className={cn("min-w-36 flex-1 win-sm:max-w-80", barBelow && "hidden win-md:flex")} />

      {importsWorking ? (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" /> importing
        </span>
      ) : null}

      {/* Left, done, blocked and in review are the state chips' and the column
          headers' counts already; these are the ones nothing else shows. */}
      <div className="hidden flex-wrap items-center gap-1.5 win-sm:ml-auto win-sm:flex">
        {stats.overdue > 0 ? <Stat label="overdue" value={stats.overdue} tint="var(--destructive)" /> : null}
        {stats.openMentions > 0 ? (
          <Hint label={`${stats.openMentions} @agent request(s) on this board still unanswered`}>
            <Badge tint="var(--primary)" className="gap-1">
              <AtSign /> {stats.openMentions} asked
            </Badge>
          </Hint>
        ) : null}
        {stats.assignedToAgents > 0 ? (
          <Hint label="Open cards assigned to an agent">
            <Badge tint="var(--primary)">Agents {stats.assignedToAgents}</Badge>
          </Hint>
        ) : null}
        {stats.assignedToMe > 0 ? (
          <Hint label="Open cards assigned to you">
            <Badge tint={kindColor("active")}>You {stats.assignedToMe}</Badge>
          </Hint>
        ) : null}
      </div>
    </div>
  );
}

/**
 * How much of the window has burned, coloured by urgency, and what is left. Nothing
 * at all for a board with no deadline: an empty bar and "no deadline" beside the
 * window label that already says so was the same fact three times.
 */
function DeadlineBar({ detail, now, className }: { detail: BoardDetail; now: number; className?: string }) {
  const boardWindow = detail.window;
  if (boardWindow.endsAt === null) return null;
  const tint = urgencyColor(boardWindow, now);
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Progress
        value={progressPercent(boardWindow, now)}
        indicatorColor={tint}
        className="h-1.5 flex-1"
        aria-label="Time used of the board's window"
      />
      <span className="shrink-0 text-xs font-semibold tabular-nums" style={{ color: tint }}>
        {remainingLabel(boardWindow, now)}
      </span>
    </div>
  );
}

/**
 * The directory as a short chip — its last folder — with the full path on hover
 * and, since a touch screen has no hover, in the menu a tap opens.
 */
function ProjectChip({ detail, canEdit, onSetProject }: { detail: BoardDetail; canEdit: boolean; onSetProject: () => void }) {
  const project = detail.project;
  const members = detail.projects ?? (project ? [project] : []);
  const many = members.length > 1;
  const folder = (hostPath: string) => hostPath.replace(/\/+$/, "").split("/").pop() || hostPath;
  const lone = project ?? members[0] ?? null;
  const name = many ? `${members.length} projects` : lone ? folder(lone.hostPath) : "no project";
  const hint = many ? members.map((member) => member.name + (member.id === project?.id ? " (default)" : "")).join(", ") : lone ? lone.hostPath : "No directory set";
  return (
    <DropdownMenu>
      <Hint label={hint}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={many ? `Projects: ${hint}` : lone ? `Project: ${lone.hostPath}` : "No project set"}
            className={cn(
              "inline-flex max-w-44 items-center gap-1 rounded-full border border-border/70 px-2 py-0.5 text-xs font-medium transition-colors hover:bg-muted/70",
              lone ? "text-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <FolderGit2 className="size-3.5 shrink-0" style={lone ? { color: "var(--kind-review)" } : undefined} />
            <span className={cn("truncate", lone && !many && "font-mono text-[11px]")}>{name}</span>
          </button>
        </DropdownMenuTrigger>
      </Hint>
      <DropdownMenuContent align="start" className="max-w-[min(22rem,calc(100vw-1.5rem))]">
        <DropdownMenuLabel>{many ? `${members.length} projects` : lone ? lone.name : "No project"}</DropdownMenuLabel>
        {many ? (
          <div className="space-y-1 px-2 pb-1.5 text-xs text-muted-foreground">
            {members.map((member) => (
              <p key={member.id} className="flex min-w-0 items-baseline gap-1.5">
                <span className="shrink-0 text-foreground">{member.name}</span>
                <span className="min-w-0 truncate font-mono text-[10px]" title={member.hostPath}>
                  {member.hostPath}
                </span>
                {member.id === project?.id ? <span className="shrink-0 text-[10px] font-medium text-primary">default</span> : null}
              </p>
            ))}
            <p className="pt-0.5">
              {project
                ? `A card with no project of its own works in ${project.name}.`
                : "No default: each card names its own project."}
            </p>
          </div>
        ) : (
          <p className="px-2 pb-1.5 text-xs text-muted-foreground">
            {project ? (
              <>
                Cards here are worked on in{" "}
                <span className="break-all font-mono text-[11px] text-foreground">{project.hostPath}</span> — an agent can
                read and change code there.
              </>
            ) : lone ? (
              <>
                Covers <span className="break-all font-mono text-[11px] text-foreground">{lone.hostPath}</span>, with no
                default: a card works there only when it names it.
              </>
            ) : (
              "No directory set — an @agent request on these cards can only answer from the board."
            )}
          </p>
        )}
        {canEdit ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onSetProject}>
              <FolderGit2 /> {members.length > 0 ? "Change projects" : "Set a project"}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const SORT_ICONS: Record<TaskSort, typeof ArrowUpDown> = {
  manual: GripVertical,
  updated: History,
  due: CalendarClock,
  created: CalendarPlus,
  priority: Flag,
};

/**
 * The sort, beside the layout toggle. Manual order shows the plain sort icon; any
 * other shows that sort's own icon with a dot, so a sorted board says so at a glance.
 */
function SortMenu({ sort, onSortChange }: { sort: TaskSort; onSortChange: (sort: TaskSort) => void }) {
  const sorted = sort !== "manual";
  const Icon = sorted ? SORT_ICONS[sort] : ArrowUpDown;
  const label = `Sort: ${sortLabel(sort)}`;
  return (
    <DropdownMenu>
      <Hint label={label}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={label}
            className={cn("relative", sorted && "bg-accent text-foreground")}
          >
            <Icon />
            {sorted ? (
              <span aria-hidden className="absolute right-1.5 top-1.5 size-1.5 rounded-full bg-primary ring-2 ring-background" />
            ) : null}
          </Button>
        </DropdownMenuTrigger>
      </Hint>
      <DropdownMenuContent align="end" className="w-64 max-w-[calc(100vw-1.5rem)]">
        <DropdownMenuLabel>Sort cards by</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={sort} onValueChange={(value) => isTaskSort(value) && onSortChange(value)}>
          {TASK_SORTS.map((entry) => {
            const ItemIcon = SORT_ICONS[entry.id];
            return (
              <DropdownMenuRadioItem key={entry.id} value={entry.id} className="items-start">
                <ItemIcon className="mt-0.5" />
                <span className="flex min-w-0 flex-col">
                  <span>{entry.label}</span>
                  <span className="text-[11px] text-muted-foreground">{entry.hint}</span>
                </span>
              </DropdownMenuRadioItem>
            );
          })}
        </DropdownMenuRadioGroup>
        {sorted ? (
          <p className="border-t border-border/60 px-2 pb-1 pt-1.5 text-[11px] text-muted-foreground">
            Done states list the most recently finished first. Drag a card to another state to move it.
          </p>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const ImportCount = ({ count }: { count: number }) =>
  count > 0 ? (
    <span className="ml-auto rounded-full bg-primary/15 px-1.5 text-[10px] font-semibold text-primary">{count}</span>
  ) : null;

/**
 * "N waiting on you": pressed, the board shows only those cards. Pressing it again
 * or its ✕ puts the rest back.
 */
const WaitingChip = ({ count, active, onToggle }: { count: number; active: boolean; onToggle: () => void }) => (
  <span
    className={cn(
      "inline-flex items-center rounded-full text-xs font-semibold transition-colors",
      active ? "bg-waiting text-waiting-foreground" : "bg-waiting/15 text-waiting hover:bg-waiting/25",
    )}
  >
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      aria-label={active ? `Showing the ${count} card(s) waiting on you — show every card` : `Show only the ${count} card(s) waiting on you`}
      className={cn("inline-flex items-center gap-1.5 rounded-full py-1 pl-2.5", active ? "pr-1" : "pr-2.5")}
    >
      <MessageCircleQuestion className="size-3.5" />
      <span className="tabular-nums">{count}</span> waiting on you
    </button>
    {active ? (
      <button
        type="button"
        onClick={onToggle}
        aria-label="Show every card"
        className="mr-1 grid size-5 place-items-center rounded-full hover:bg-waiting-foreground/15"
      >
        <X className="size-3" />
      </button>
    ) : null}
  </span>
);

const Stat = ({ label, value, tint }: { label: string; value: number; tint?: string }) => (
  <span
    className={cn("inline-flex items-baseline gap-1 rounded-full bg-muted/70 px-2 py-0.5 text-[11px]")}
    style={tint ? { color: tint, backgroundColor: `color-mix(in oklab, ${tint} 13%, transparent)` } : undefined}
  >
    <span className="font-semibold tabular-nums">{value}</span>
    <span className="opacity-75">{label}</span>
  </span>
);
